import { useField, useFormikContext } from 'formik';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Chip from '../../base/Chip';
import Icon from '../../base/Icon';
import Text from '../../base/Text';
import Tooltip from '../../base/Tooltip';
import { Div } from '../../helpers/StyledUtils';
import JSONBrowser from '../../JSONBrowser';
interface Props {
  label?: string;
  name: string;
  placeholder: string;
  required?: boolean;
  bordered?: boolean;
  max_tokens?: number;
  useSuggestions: () => {
    suggestions: string[];
    getValues: (fieldPath: string) => unknown[];
  };
}
type SelectionMode = 'keyboard' | 'mouse';
const StyledFieldContainer = styled(Div)<{
  $bordered?: boolean;
  $error?: boolean;
}>(({
  theme,
  $bordered,
  $error
}) => css`
    cursor: text;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};

    ${$bordered && css`
      box-sizing: border-box;
      width: 100%;
      min-width: 100%;
      min-height: ${theme.spacing(8)};
      padding: ${theme.spacing(1.5)} ${theme.spacing(3)};
      border-radius: ${theme.radius.normal};
      box-shadow: ${theme.shadows.input.neutral};

      &:focus-within {
        box-shadow: ${theme.shadows.input.focused};
      }
    `}

    ${$bordered && $error && css`
      textarea:not(:focus) {
        ::placeholder {
          color: ${theme.colors.on.neutral.danger};
        }
      }

      box-shadow: ${theme.shadows.input.error};
      background-color: ${theme.colors.surface.container.danger};

      &:focus-within {
        box-shadow: ${theme.shadows.input.focused};
        background-color: transparent;
      }
    `}
  `);
const StyledTextarea = styled.textarea(({
  theme
}) => css`
    font-size: ${theme.pxToRem(theme.font_sizes.s.font_size)};
    line-height: ${theme.pxToRem(theme.font_sizes.s.line_height)};
    font-family:
      JetBrains Mono,
      monospace;
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    border-radius: unset;
    padding: 0;
    resize: none;

    &:focus {
      outline: none;
    }
  `);
const StyledFocusArea = styled(Div)`
  position: absolute;
  cursor: text;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;
const StyledDropdown = styled(Div)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({
  theme
}) => theme.colors.surface.base.surface};
  border-radius: ${({
  theme
}) => theme.radius.normal};
  box-shadow: ${({
  theme
}) => theme.elevation[2]};
  z-index: ${({
  theme
}) => theme.zindex.dropdown};
  max-height: 180px;
  overflow-y: scroll;
  overflow-x: visible;
  margin-top: ${({
  theme
}) => theme.spacing(1)};
  padding: ${({
  theme
}) => theme.spacing(1)};

  button {
    &:focus-visible {
      outline: none;
    }
  }
`;
const StyledPreviewValuesList = styled(Div)(({
  theme
}) => css`
    max-height: 160px;
    overflow-y: auto;
    margin-left: -${theme.spacing(2)};
    margin-right: -${theme.spacing(2)};
  `);
const StyledPreviewValue = styled(Div)(({
  theme
}) => css`
    min-width: 0;
    border-bottom: 1px solid ${theme.colors.outline.neutral};
    padding-left: ${theme.spacing(3)};
    padding-right: ${theme.spacing(3)};
    padding-top: ${theme.spacing(2)};
    padding-bottom: ${theme.spacing(2)};

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
      border-bottom: none;
    }
  `);
const StyledSuggestionButton = styled.button<{
  $active?: boolean;
  $selectionMode: SelectionMode;
}>(({
  theme,
  $active,
  $selectionMode
}) => css`
    display: block;
    width: 100%;
    padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
    box-sizing: border-box;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
    outline: none;
    border: none;
    border-radius: ${theme.radius.normal};
    color: unset;
    background-color: ${$active ? theme.colors.surface.base.hover.neutral : 'unset'};

    ${$selectionMode === 'mouse' && css`
      &:hover {
        background-color: ${theme.colors.surface.base.hover.neutral};
      }
    `}
  `);
const JSONFieldSelectorInput: React.FC<Props> = ({
  label,
  name,
  placeholder,
  required = false,
  bordered = false,
  max_tokens,
  useSuggestions
}) => {
  const [field, {
    error,
    touched
  }, helpers] = useField(name);
  const {
    submitCount
  } = useFormikContext();
  const [inputValue, setInputValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [preview_values, setPreviewValues] = useState<unknown[]>([]);
  const [selected_suggestion_index, setSelectedSuggestionIndex] = useState<number | null>(null);
  const [selection_mode, setSelectionMode] = useState<SelectionMode>('mouse');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const skip_blur_commit_ref = useRef(false);
  const single_token = max_tokens === 1;
  const has_error = (touched || submitCount > 0) && !!error;

  // Parse tokens from field value
  const tokens = field.value ? field.value.split(',').map((token: string) => token.trim()).filter(Boolean) : [];

  // Show limited tokens when not focused
  const max_preview_tokens = 3;
  const visible_tokens = focused ? tokens : tokens.slice(0, max_preview_tokens);
  const hidden_count = focused ? 0 : Math.max(0, tokens.length - max_preview_tokens);

  // Get suggestions from hook
  const suggestions_data = useSuggestions();
  const suggestions = suggestions_data.suggestions;
  const filtered_suggestions = suggestions.filter(suggestion => suggestion.toLowerCase().includes(inputValue.toLowerCase()) && !tokens.includes(suggestion));
  const selected_suggestion = selected_suggestion_index !== null ? filtered_suggestions[selected_suggestion_index] : null;
  const autoResize = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };
  const getSuggestionValues = (suggestion_0: string) => {
    return suggestions_data.getValues(suggestion_0);
  };
  useEffect(() => {
    if (textareaRef.current && (!single_token || focused)) {
      if (!single_token) {
        autoResize(textareaRef.current);
      }
      textareaRef.current.focus();
    }
  }, [inputValue, focused, single_token]);
  useEffect(() => {
    if (!focused || filtered_suggestions.length === 0) {
      setSelectedSuggestionIndex(null);
      return;
    }
    setSelectedSuggestionIndex(current_index => current_index !== null && current_index < filtered_suggestions.length ? current_index : 0);
  }, [filtered_suggestions.length, focused]);
  const addToken = (token_0: string) => {
    if (token_0 && (single_token || !tokens.includes(token_0))) {
      helpers.setTouched(true);
      const new_tokens = single_token ? [token_0] : [...tokens, token_0].slice(0, max_tokens);
      helpers.setValue(new_tokens.join(', '));
      if (single_token) {
        setInputValue(token_0);
        setFocused(false);
        textareaRef.current?.blur();
      }
    }
  };
  const commitToken = (token_1: string) => {
    skip_blur_commit_ref.current = true;
    addToken(token_1);
  };
  const removeToken = (token_2: string) => {
    helpers.setTouched(true);
    const new_tokens_0 = tokens.filter(t => t !== token_2);
    helpers.setValue(new_tokens_0.join(', '));
    // Keep focus after removal
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // Handle textarea changes
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    setSelectedSuggestionIndex(null);
    if (!single_token) {
      autoResize(e.target);
    }
  };

  // Handle key events
  const handleKeyDown = (e_0: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e_0.key === 'ArrowDown' || e_0.key === 'ArrowUp') {
      if (filtered_suggestions.length === 0) {
        return;
      }
      e_0.preventDefault();
      setSelectionMode('keyboard');
      setSelectedSuggestionIndex(current_index_0 => {
        const suggestion_index = current_index_0 ?? -1;
        if (e_0.key === 'ArrowDown') {
          return suggestion_index === -1 ? 0 : (suggestion_index + 1) % filtered_suggestions.length;
        }
        return suggestion_index <= 0 ? filtered_suggestions.length - 1 : suggestion_index - 1;
      });
    } else if (e_0.key === 'Enter') {
      e_0.preventDefault();
      const suggestion_to_add = selected_suggestion ?? filtered_suggestions[0];
      if (suggestion_to_add) {
        commitToken(suggestion_to_add);
        if (!single_token) {
          setInputValue('');
        }
        setSelectedSuggestionIndex(null);
      } else if (inputValue.trim()) {
        // Only add current input as token if there are no suggestions
        commitToken(inputValue.trim());
        if (!single_token) {
          setInputValue('');
        }
        setSelectedSuggestionIndex(null);
      }
    } else if (e_0.key === ' ' && inputValue.trim()) {
      e_0.preventDefault();
      commitToken(inputValue.trim());
      if (!single_token) {
        setInputValue('');
      }
      setSelectedSuggestionIndex(null);
    } else if (e_0.key === 'Backspace' && !inputValue && tokens.length > 0) {
      e_0.preventDefault();
      removeToken(tokens[tokens.length - 1]);
      textareaRef.current?.focus();
      setSelectedSuggestionIndex(null);
    }
  };
  const handleFocus = () => {
    setFocused(true);
    if (single_token && tokens.length > 0) {
      setInputValue(tokens[0]);
    }
    if (selected_suggestion_index === null && filtered_suggestions.length > 0) {
      setSelectedSuggestionIndex(0);
    }
  };
  const handleBlur = (e_1: React.FocusEvent<HTMLTextAreaElement>) => {
    // Check if focus is moving to a dropdown item
    const related_target = e_1.relatedTarget as HTMLElement;
    const is_dropdown_item = related_target?.closest('[data-dropdown-item]');
    if (!is_dropdown_item) {
      if (skip_blur_commit_ref.current) {
        skip_blur_commit_ref.current = false;
        return;
      }
      setFocused(false);
      // Add current input as token on blur
      if (inputValue.trim()) {
        addToken(inputValue.trim());
        if (!single_token) {
          setInputValue('');
        }
      }
    }
  };

  // Handle container click
  const handleContainerClick = () => {
    setFocused(true);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };
  useEffect(() => {
    if (selected_suggestion) {
      setPreviewValues(getSuggestionValues(selected_suggestion));
    }
  }, [selected_suggestion]);
  const handleSuggestionMouseMove = (index: number) => {
    setSelectionMode('mouse');
    setSelectedSuggestionIndex(index);
  };
  const handleSuggestionMouseLeave = () => {
    if (selection_mode === 'mouse') {
      setSelectedSuggestionIndex(null);
    }
  };
  return <Div style={{
    position: 'relative'
  }}>
      {label && <Text as="label" htmlFor={name} size="xs" semi muted m={{
      b: 1
    }}>
          {label}
          {required && <Text as="span" danger>
              *
            </Text>}
        </Text>}

      <StyledFieldContainer $bordered={bordered} $error={has_error}>
        {visible_tokens.length > 0 && !(single_token && focused) && <Div flex={{
        align: 'center',
        wrap: !single_token,
        gap: 2
      }} style={single_token ? {
        minWidth: 0,
        overflow: 'hidden'
      } : undefined}>
            {visible_tokens.map((token_3, index_0) => single_token ? <Text key={`${token_3}-${index_0}`} mono size="s" dark ellipsis>
                  {token_3}
                </Text> : <Chip key={`${token_3}-${index_0}`} mono style={{
          zIndex: 2
        }} label={token_3} icon="close" onClick={() => {
          removeToken(token_3);
        }} />)}
            {hidden_count > 0 && <Text size="s" muted onClick={e_2 => e_2?.stopPropagation()}>
                + {hidden_count} more
              </Text>}
          </Div>}

        {!focused && <StyledFocusArea onClick={handleContainerClick} />}

        {(focused || tokens.length === 0) && <StyledTextarea id={name} ref={textareaRef} value={inputValue} onChange={handleChange} onKeyDown={handleKeyDown} onFocus={handleFocus} onBlur={handleBlur} placeholder={tokens.length === 0 ? placeholder : ''} rows={1} />}
      </StyledFieldContainer>

      {focused && filtered_suggestions.length > 0 && <StyledDropdown>
          <Text size="xs" semi muted m={{
        b: 1,
        t: 1,
        x: 2
      }}>
            Detected fields
          </Text>
          {filtered_suggestions.map((suggestion_1, i) => {
        const suggestion_is_selected = selected_suggestion_index === i;
        return <Tooltip fill key={suggestion_1} side="right" open={suggestion_is_selected} element={<Div style={{
          width: '300px'
        }}>
                    <Text size="xs" semi muted m={{
            b: 1
          }} p={{
            x: 1
          }}>
                      Recent field value{preview_values.length > 1 ? 's' : ''}
                    </Text>
                    {preview_values.length > 0 ? <StyledPreviewValuesList>
                        {preview_values.map((preview_value, index_1) => <StyledPreviewValue key={index_1}>
                            <JSONBrowser json={preview_value as any} compact />
                          </StyledPreviewValue>)}
                      </StyledPreviewValuesList> : <Text size="xs" muted p={{
            x: 1
          }}>
                        No sample values
                      </Text>}
                  </Div>}>
                <StyledSuggestionButton data-dropdown-item type="button" tabIndex={i === 0 ? -1 : undefined} $active={suggestion_is_selected} $selectionMode={selection_mode} onMouseMove={() => handleSuggestionMouseMove(i)} onMouseLeave={handleSuggestionMouseLeave} onFocus={() => {
            setSelectedSuggestionIndex(i);
            setPreviewValues(getSuggestionValues(suggestion_1));
          }} onBlur={e_3 => {
            const related_target_0 = e_3.relatedTarget as HTMLElement;
            const is_dropdown_or_textarea = related_target_0?.closest('[data-dropdown-item]') || related_target_0 === textareaRef.current;
            if (!is_dropdown_or_textarea) {
              setFocused(false);
            }
          }} onClick={() => {
            commitToken(suggestion_1);
            setSelectedSuggestionIndex(null);
            if (!single_token) {
              setInputValue('');
              textareaRef.current?.focus();
            }
          }}>
                  <Text mono size="s" dark>
                    {suggestion_1}
                  </Text>
                </StyledSuggestionButton>
              </Tooltip>;
      })}
        </StyledDropdown>}
      {has_error && <Text m={{
      t: 1,
      b: 0
    }} size="xs" as="p" danger flex={{
      align: 'center',
      gap: 1
    }}>
          <Icon icon="info" />
          {error}
        </Text>}
    </Div>;
};
export default JSONFieldSelectorInput;